"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useSyncExternalStore } from "react";

import { useSessionContext } from "@/components/session/SessionProvider";
import { Button } from "@/components/ui/button";
import { useOrgSubscription, usePortalMutation } from "@/hooks/useBilling";
import { useBillingConfig } from "@/hooks/useBillingConfig";
import { formatBillingDate } from "@/lib/billing-format";
import { isSubscriptionLocked, subscribeToSubscriptionLock } from "@/lib/billing-lock";
import { logoutAndRedirect } from "@/lib/session/client";
import { ORG_OWNER_SCOPE } from "@/types/billing";

// Same exclusions as UsageConsentGate: onboarding has its own plan step,
// auth/oauth pages run before a session exists, previews use fixture sessions.
const EXCLUDED_PREFIXES = ["/onboarding", "/login", "/oauth", "/preview"];

export const BILLING_SETTINGS_PATH = "/settings/billing";

function useLockedByApi(): boolean {
  return useSyncExternalStore(subscribeToSubscriptionLock, isSubscriptionLocked, () => false);
}

/**
 * App-wide subscription status: a banner while the org is `past_due` and a
 * full-screen lock while it is `locked` (docs/plan/stripe-subscriptions.md,
 * "Payment failure and grace"). Renders nothing when billing is disabled, on
 * excluded routes, or while the subscription has not loaded.
 */
export function SubscriptionStatusGate() {
  const pathname = usePathname();
  const { session } = useSessionContext();
  const billing = useBillingConfig();

  const excluded = !pathname || EXCLUDED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
  const authenticated = session.status === "authenticated";
  const scopes = authenticated ? (session.profile.scopes ?? []) : [];
  const isOwner = scopes.includes(ORG_OWNER_SCOPE);

  const subscriptionQuery = useOrgSubscription({
    enabled: authenticated && !excluded && billing.enabled,
    // While locked or past due, keep looking for the unlock.
    refetchInterval: 60_000,
  });
  const lockedByApi = useLockedByApi();

  if (!authenticated || excluded) return null;

  const subscription = subscriptionQuery.data;
  const locked = lockedByApi || subscription?.state === "locked";
  if (locked) {
    return <SubscriptionLockOverlay isOwner={isOwner} />;
  }

  if (billing.enabled && subscription?.state === "past_due") {
    return (
      <PastDueBanner
        graceUntil={subscription.graceUntil ?? null}
        isOwner={isOwner}
        onBillingPage={pathname === BILLING_SETTINGS_PATH}
      />
    );
  }

  return null;
}

/** Persistent "payment failed" banner. Exported so previews can render it directly. */
export function PastDueBanner({
  graceUntil,
  isOwner,
  onBillingPage = false,
}: {
  graceUntil: string | null;
  isOwner: boolean;
  onBillingPage?: boolean;
}) {
  const deadline = graceUntil ? formatBillingDate(graceUntil) : "the end of the grace period";
  return (
    <div
      role="alert"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-[color:var(--token-accent-warning-border-strong)] bg-[color:var(--token-accent-warning-surface)] px-4 py-3 text-sm text-[color:var(--token-accent-warning-foreground-muted)]"
      style={{ paddingBottom: "max(var(--space-md), env(safe-area-inset-bottom))" }}
    >
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-medium">
          Payment failed. Update your card before {deadline} to keep access.
        </p>
        {isOwner ? (
          onBillingPage ? null : (
            <Link
              href={BILLING_SETTINGS_PATH}
              className="shrink-0 rounded-[var(--radius-full)] border border-current px-4 py-1.5 font-medium underline-offset-4 hover:underline"
            >
              Go to billing
            </Link>
          )
        ) : (
          <p className="shrink-0">Contact your organization owner.</p>
        )}
      </div>
    </div>
  );
}

/** Full-screen lock. Exported so previews can render it directly. */
export function SubscriptionLockOverlay({ isOwner }: { isOwner: boolean }) {
  const portal = usePortalMutation();
  const [portalError, setPortalError] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  async function handleOpenPortal() {
    setPortalError(null);
    try {
      const { portalUrl } = await portal.mutateAsync();
      window.location.assign(portalUrl);
    } catch (error) {
      setPortalError(
        error instanceof Error ? error.message : "Could not open the payment page. Try again.",
      );
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-background/80 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscription-lock-title"
      aria-describedby="subscription-lock-description"
    >
      <div className="my-auto flex w-full max-w-lg flex-col gap-4 rounded-lg border border-border bg-card p-6 shadow-lg">
        <h1 id="subscription-lock-title" className="text-lg font-semibold">
          Your organization is locked
        </h1>
        <p id="subscription-lock-description" className="text-sm text-muted-foreground">
          A payment for your organization&apos;s subscription failed and the grace period has
          ended. Namespaces are paused and the Reality Model cannot be used until the payment is
          fixed.
        </p>
        {isOwner ? (
          <>
            <p className="text-sm text-muted-foreground">
              Update the card on file to unlock your organization. Access returns as soon as the
              payment goes through.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" onClick={handleOpenPortal} disabled={portal.isPending}>
                {portal.isPending ? "Opening…" : "Update payment details"}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={signingOut}
                onClick={() => {
                  setSigningOut(true);
                  logoutAndRedirect();
                }}
              >
                Sign out
              </Button>
            </div>
            {portalError ? (
              <p role="alert" className="text-sm text-destructive">
                {portalError}
              </p>
            ) : null}
          </>
        ) : (
          <>
            <p className="text-sm font-medium text-foreground">
              Contact your organization owner to restore access.
            </p>
            <div>
              <Button
                type="button"
                variant="outline"
                disabled={signingOut}
                onClick={() => {
                  setSigningOut(true);
                  logoutAndRedirect();
                }}
              >
                Sign out
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
